import React, { useRef, useImperativeHandle, forwardRef, useEffect } from "react";
import FourColumnRowSection from "components/common/FourColumnRowSection/FourColumnRowSection";
import { Field, FieldRef } from "../Field/Field";
import { StaffState } from "types/staff-edit.types";
import { CustomerRules } from "utils/formRules";
import { FileData, ImagePayload } from "types/image.type";
import UploadImageButton from "components/common/UploadImageButton/UploadButton";

interface DriverLicenseSectionProps {
  data: StaffState["driverLicense"];
  onUpdate: (path: string, value: string | FileData[] | ImagePayload[]) => void;
  handleUpload?: (file: File, path: string) => Promise<void>;
  isUploading?: boolean;
}

export interface DriverLicenseSectionRef {
  validateAll: () => Promise<boolean>;
}

export const DriverLicenseSection = forwardRef<
  DriverLicenseSectionRef,
  DriverLicenseSectionProps
>(({ data, onUpdate }, ref) => {
  const licenseClassRef = useRef<FieldRef>(null);
  const licenseNumberRef = useRef<FieldRef>(null);
  const licenseExpiredDateRef = useRef<FieldRef>(null);
  const vehicleRef = useRef<FieldRef>(null);

  // 🌟 Tách làm 2 Refs để giữ state cho Mặt trước và Mặt sau riêng biệt
  const frontImagesRef = useRef<ImagePayload[]>(data?.frontLicenseImages || []);
  const backImagesRef = useRef<ImagePayload[]>(data?.backLicenseImages || []);

  // Cập nhật ref khi data từ cha truyền xuống thay đổi
  useEffect(() => {
    frontImagesRef.current = data?.frontLicenseImages || [];
    backImagesRef.current = data?.backLicenseImages || [];
  }, [data?.frontLicenseImages, data?.backLicenseImages]);

  useImperativeHandle(ref, () => ({
    validateAll: async () => {
      const results = await Promise.all([
        licenseClassRef.current?.validate(),
        licenseNumberRef.current?.validate(),
        licenseExpiredDateRef.current?.validate(),
        vehicleRef.current?.validate(),
      ]);
      return results.every((r) => r === true);
    },
  }));

  return (
    <FourColumnRowSection
      leftTitle="Giấy phép lái xe"
      rightTitle=""
      rows={[
        {
          leftLabel: "Hạng bằng lái",
          leftValue: (
            <Field
              ref={licenseClassRef}
              path="driverLicense.licenseClass"
              value={data?.licenseClass}
              onUpdate={onUpdate}
              options={["B1", "B2", "C", "D", "E", "FC"]}
              placeholder="Chọn hạng bằng"
              rules={CustomerRules}
            />
          ),
          rightLabel: "Số GPLX",
          rightValue: (
            <Field
              ref={licenseNumberRef}
              path="driverLicense.licenseNumber"
              value={data?.licenseNumber}
              onUpdate={onUpdate}
              placeholder="Nhập số GPLX"
              rules={CustomerRules}
            />
          ),
        },
        {
          leftLabel: "Hạn bằng lái",
          leftValue: (
            <Field
              ref={licenseExpiredDateRef}
              type="date"
              path="driverLicense.licenseExpiredDate"
              value={data?.licenseExpiredDate}
              onUpdate={onUpdate}
              placeholder="Chọn ngày hết hạn"
              rules={CustomerRules}
            />
          ),
          rightLabel: "Đầu kéo",
          rightValue: (
            <Field
              ref={vehicleRef}
              path="driverLicense.vehicle"
              value={data?.vehicle}
              onUpdate={onUpdate}
              placeholder="Nhập biển số xe"
              rules={CustomerRules}
            />
          ),
        },
        {
          // 👈 CỘT TRÁI: ẢNH MẶT TRƯỚC
          leftLabel: "Ảnh mặt trước",
          leftValue: (
            <div className="flex items-center gap-2">
              <UploadImageButton
                label="Tải lên mặt trước"
                maxCount={1} // Ép chỉ được chọn 1 ảnh cho mặt trước
                isDisabled={false}
                imagesData={data?.frontLicenseImages || []}
                setImagesData={(action) => {
                  const currentImages = frontImagesRef.current;
                  const updatedImages: ImagePayload[] = typeof action === 'function' 
                    ? action(currentImages) 
                    : action;

                  frontImagesRef.current = updatedImages;
                  // Map đúng key để lưu vào data tổng ở cha
                  onUpdate("driverLicense.frontLicenseImages", updatedImages);
                }}
              />
              {data?.frontLicenseImages && data.frontLicenseImages.length > 0 && (
                <span className="text-xs text-green-600 font-medium mt-1 inline-block">
                  ✓ Đã tải lên
                </span>
              )}
            </div>
          ),
          // 👈 CỘT PHẢI: ẢNH MẶT SAU
          rightLabel: "Ảnh mặt sau",
          rightValue: (
            <div className="flex items-center gap-2">
              <UploadImageButton
                label="Tải lên mặt sau"
                maxCount={1} // Ép chỉ được chọn 1 ảnh cho mặt sau
                isDisabled={false}
                imagesData={data?.backLicenseImages || []}
                setImagesData={(action) => {
                  const currentImages = backImagesRef.current;
                  const updatedImages: ImagePayload[] = typeof action === 'function' 
                    ? action(currentImages) 
                    : action;

                  backImagesRef.current = updatedImages;
                  // Map đúng key để lưu vào data tổng ở cha
                  onUpdate("driverLicense.backLicenseImages", updatedImages);
                }}
              />
              {data?.backLicenseImages && data.backLicenseImages.length > 0 && (
                <span className="text-xs text-green-600 font-medium mt-1 inline-block">
                  ✓ Đã tải lên
                </span>
              )}
            </div>
          ),
        },
      ]}
    />
  );
});

DriverLicenseSection.displayName = "DriverLicenseSection";